import React, { useState } from "react";
import {
  Check,
  CheckCircle2,
  Clock,
  Ban as BanIcon,
  ExternalLink,
  Trash2,
  EyeOff,
  Plus,
} from "lucide-react";
import { useCase } from "../../context/CaseContext";

interface ChecklistGroupProps {
  title: string;
  cat: string;
  taskList: any[];
}

const ChecklistGroup: React.FC<ChecklistGroupProps> = ({
  title,
  cat,
  taskList,
}) => {
  const {
    items,
    toggleChecklist,
    blockedItems,
    updateBlockedItem,
    hiddenItems,
    updateHiddenItem,
    showHiddenItems,
    itemMeta,
    handleAddCustomTask,
    handleDeleteCustomTask,
    platformIds,
    handleUpdatePlatformId,
    itemStatus,
    handleVerify,
  } = useCase();

  // Custom tasks are always shown, otherwise follow global setting
  const effectiveShowHidden = cat === "custom" || showHiddenItems;

  const visibleTasks = taskList.filter(
    (t: any) => !hiddenItems[t.id] || effectiveShowHidden,
  );
  const [newTask, setNewTask] = useState("");

  const handleAdd = () => {
    if (!newTask.trim()) return;
    handleAddCustomTask(cat, newTask);
    setNewTask("");
  };

  if (visibleTasks.length === 0 && cat !== "custom") return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden mb-6">
      <div className="bg-slate-50 dark:bg-slate-900/50 px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {visibleTasks.map((task: any) => {
          const key = task.id;
          const isChecked = items[key];
          const isBlocked = blockedItems[key];
          const meta = itemMeta[key];
          const isHidden = hiddenItems[key];
          const isCustom = key.startsWith(`task_${cat}_`);

          const status = itemStatus?.[key]; // 'pending_review' | 'verified' | null
          const isVerified = status === "verified";
          const isPending = status === "pending_review";

          return (
            <div
              key={key}
              className={`group flex items-center justify-between p-4 transition-all border-l-[3px] border-transparent
      ${isBlocked ? "bg-slate-50 dark:bg-slate-900/40 opacity-75" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"}
      ${isHidden ? "opacity-50 grayscale" : ""}
      ${isVerified ? "bg-green-50/50 dark:bg-green-900/10 border-l-green-500" : ""}
      ${!isVerified && isPending ? "bg-amber-50/50 dark:bg-amber-900/10 border-l-amber-500" : ""}
      ${!isVerified && !isPending && isChecked ? "bg-blue-50/30 dark:bg-blue-900/5" : ""}
      `}
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="pt-0.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleChecklist(key);
                    }}
                    disabled={isBlocked || isVerified}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-all
                        ${
                          isVerified
                            ? "bg-blue-600 border-blue-600 opacity-60 cursor-not-allowed"
                            : isChecked
                              ? "bg-blue-600 border-blue-600"
                              : "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 hover:border-blue-400"
                        }`}
                  >
                    {(isChecked || isVerified) && (
                      <Check size={14} className="text-white" />
                    )}
                  </button>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div
                      className={`text-sm font-medium transition-all cursor-pointer select-none
                    ${isChecked || isVerified ? "text-slate-400 line-through" : "text-slate-700 dark:text-slate-200"}
                    ${isBlocked ? "text-slate-500" : ""}`}
                      onClick={() =>
                        !isBlocked && !isVerified && toggleChecklist(key)
                      }
                    >
                      {task.label}
                    </div>

                    {/* ROLE BADGE */}
                    {task.role === "customer" && (
                      <span className="text-[9px] font-bold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-100 dark:border-indigo-800 uppercase">
                        Client
                      </span>
                    )}

                    {/* STATUS BADGES */}
                    {isVerified && (
                      <span className="text-[9px] font-bold bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-1.5 py-0.5 rounded uppercase flex items-center gap-1 border border-green-100 dark:border-green-800">
                        <CheckCircle2 size={10} /> Verified
                      </span>
                    )}

                    {isPending && (
                      <span className="text-[9px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase flex items-center gap-1 animate-pulse border border-amber-100 dark:border-amber-800">
                        <Clock size={10} /> Review Needed
                      </span>
                    )}

                    {isBlocked && (
                      <span className="text-[9px] font-bold bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded uppercase flex items-center gap-1 border border-red-100 dark:border-red-800">
                        <BanIcon size={10} /> Blocked
                      </span>
                    )}
                  </div>

                  {/* METADATA */}
                  {isChecked && meta && (
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 flex items-center gap-1 animate-in fade-in">
                      <CheckCircle2 size={10} /> Checked by{" "}
                      <strong className="font-medium">{meta.user}</strong> on{" "}
                      {new Date(meta.time).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* VERIFY BUTTON (Only if Pending) */}
                {isPending && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerify(key, true);
                    }}
                    className="bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded text-[10px] font-bold uppercase transition-colors mr-2 flex items-center gap-1"
                  >
                    <CheckCircle2 size={12} /> Approve
                  </button>
                )}

                {/* ID Input Field */}
                {task.hasIdField && (
                  <div onClick={(e) => e.stopPropagation()} className="mr-2">
                    <input
                      type="text"
                      placeholder={task.idLabel || "ID"}
                      className="text-[10px] border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-900 dark:text-white outline-none focus:border-blue-500 w-28 placeholder:text-gray-400"
                      value={platformIds?.[task.id] || ""}
                      onChange={(e) =>
                        handleUpdatePlatformId &&
                        handleUpdatePlatformId(task.id, e.target.value)
                      }
                    />
                  </div>
                )}

                {/* Dynamic External Link Button */}
                {task.externalLink && (
                  <a
                    href={task.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] font-bold bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 hover:bg-blue-700 transition-colors mr-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open Container <ExternalLink size={10} />
                  </a>
                )}

                <div
                  className={`flex gap-2 transition-opacity ${isBlocked ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateBlockedItem(key, !isBlocked);
                    }}
                    className={`p-1 rounded ${isBlocked ? "text-red-600 bg-red-100 hover:bg-red-200 dark:bg-red-900/30" : "text-gray-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400"}`}
                    title={isBlocked ? "Unblock Item" : "Block Item"}
                  >
                    <BanIcon size={16} />
                  </button>
                  {isCustom ? (
                    <button
                      onClick={() => handleDeleteCustomTask(cat, key)}
                      className="p-1 text-gray-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400"
                      title="Delete Custom Task"
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateHiddenItem(key, !isHidden);
                      }}
                      className="p-1 text-gray-300 hover:text-gray-500 dark:text-slate-600 dark:hover:text-slate-400"
                      title="Hide Item"
                    >
                      <EyeOff size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 bg-gray-50 dark:bg-slate-900/30 border-t border-gray-100 dark:border-slate-800 flex gap-2">
        <input
          className="flex-1 text-xs bg-transparent border-none outline-none dark:text-slate-300 pl-2 placeholder:text-gray-400 dark:placeholder:text-slate-600"
          placeholder={`Add task to ${title}...`}
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <button
          onClick={handleAdd}
          className="text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-slate-800 p-1.5 rounded transition-colors"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
};

export default ChecklistGroup;
